import { useState, useEffect, useCallback } from "react";
import { Search, Plus, Upload, Users, Trash2, ArrowDownAZ, ArrowUpZA, Clock } from "lucide-react";
import AppShell from "../components/layout/AppShell";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { ClientFormModal, DeleteClientModal } from "../components/clients/ClientModals";
import { ImportModal } from "../components/clients/ImportModal";
import { ClientsTable, ClientsMobile } from "../components/clients/ClientsTable";
import LoyaltyToggle from "../components/clients/LoyaltyToggle";

function WipeClientsButton({ onDone }) {
  var confirmState = useState(false);
  var loadingState = useState(false);
  var resultState = useState(null);

  async function handleWipe() {
    loadingState[1](true);
    try {
      var res = await api.post("/clients/wipe-all-clients");
      resultState[1](res.data);
      confirmState[1](false);
      if (onDone) onDone();
    } catch (err) {
      resultState[1]({ error: err.response?.data?.detail || "Erro ao limpar" });
    } finally { loadingState[1](false); }
  }

  if (resultState[0]) {
    var r = resultState[0];
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
        <p className="text-sm text-green-700">
          {r.error ? r.error : "Removidos: " + r.clients_deleted + " clientes, " + r.products_deleted + " produtos, " + r.documents_deleted + " documentos"}
        </p>
        <button onClick={function () { resultState[1](null); }} className="text-xs text-green-600 hover:underline">OK</button>
      </div>
    );
  }

  if (confirmState[0]) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex flex-col sm:flex-row items-start sm:items-center gap-2" data-testid="wipe-confirm">
        <p className="text-sm text-red-700 flex-1">Isso vai apagar TODOS os clientes importados e seus produtos. Tem certeza?</p>
        <div className="flex gap-2">
          <button onClick={function () { confirmState[1](false); }} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50">Cancelar</button>
          <button onClick={handleWipe} disabled={loadingState[0]} data-testid="wipe-confirm-button"
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            {loadingState[0] ? "Limpando..." : "Confirmar Exclusao"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button onClick={function () { confirmState[1](true); }} data-testid="wipe-clients-button"
      className="flex items-center gap-2 px-4 py-2.5 border border-red-200 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 whitespace-nowrap">
      <Trash2 className="w-4 h-4" />Limpar Base
    </button>
  );
}

export default function ClientsManagePage() {
  var auth = useAuth();
  var isAdmin = auth.user?.role === "admin";
  var isSuperAdmin = isAdmin && auth.user?.email === "admin@methoscorretora.com.br";
  var clientsState = useState([]);
  var totalState = useState(0);
  var loadingState = useState(true);
  var searchState = useState("");
  var pageState = useState(1);
  var showFormState = useState(false);
  var editingState = useState(null);
  var deletingState = useState(null);
  var showImportState = useState(false);
  var sortState = useState("az");
  var selectedState = useState([]);
  var deletingBatchState = useState(false);

  var fetchClients = useCallback(async function() {
    loadingState[1](true);
    try {
      var params = new URLSearchParams();
      if (searchState[0]) params.append("search", searchState[0]);
      params.append("sort", sortState[0]);
      params.append("page", pageState[0]);
      params.append("limit", "20");
      var res = await api.get("/clients?" + params.toString());
      clientsState[1](res.data.clients || []);
      totalState[1](res.data.total || 0);
    } catch(err) { console.error(err); }
    finally { loadingState[1](false); }
  }, [searchState[0], pageState[0], sortState[0]]);

  useEffect(function() { fetchClients(); }, [fetchClients]);

  async function handleDelete(id) {
    try { await api.delete("/clients/" + id); deletingState[1](null); fetchClients(); }
    catch(err) { console.error(err); }
  }

  function handleSave() { showFormState[1](false); editingState[1](null); fetchClients(); }

  var totalPages = Math.ceil(totalState[0] / 20);

  function onEdit(c) { editingState[1](c); showFormState[1](true); }
  function onDel(c) { deletingState[1](c); }

  function toggleSelect(id) {
    selectedState[1](function(prev) {
      return prev.indexOf(id) !== -1 ? prev.filter(function(x) { return x !== id; }) : prev.concat([id]);
    });
  }

  function toggleSelectAll() {
    selectedState[1](function(prev) {
      return prev.length === clientsState[0].length ? [] : clientsState[0].map(function(c) { return c.id; });
    });
  }

  async function handleBulkDelete() {
    if (selectedState[0].length === 0) return;
    deletingBatchState[1](true);
    try {
      await api.post("/clients/delete-batch", { ids: selectedState[0] });
      selectedState[1]([]);
      fetchClients();
    } catch(err) { console.error(err); }
    finally { deletingBatchState[1](false); }
  }

  return (
    <AppShell activePage="clientes" title="Clientes">
      <div data-testid="clients-manage-page">
        {showFormState[0] && <ClientFormModal client={editingState[0]} onClose={function() { showFormState[1](false); editingState[1](null); }} onSave={handleSave} />}
        {deletingState[0] && <DeleteClientModal client={deletingState[0]} onClose={function() { deletingState[1](null); }} onConfirm={handleDelete} />}
        {showImportState[0] && <ImportModal onClose={function() { showImportState[1](false); }} onSave={function() { showImportState[1](false); fetchClients(); }} />}

        {isSuperAdmin && selectedState[0].length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center justify-between" data-testid="bulk-delete-bar">
            <p className="text-sm text-red-700 font-medium">{selectedState[0].length} cliente{selectedState[0].length > 1 ? "s" : ""} selecionado{selectedState[0].length > 1 ? "s" : ""}</p>
            <div className="flex gap-2">
              <button onClick={function() { selectedState[1]([]); }} data-testid="bulk-cancel-button"
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50">Cancelar</button>
              <button onClick={handleBulkDelete} disabled={deletingBatchState[0]} data-testid="bulk-delete-button"
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5">
                <Trash2 className="w-3.5 h-3.5" />{deletingBatchState[0] ? "Excluindo..." : "Excluir Selecionados"}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl lg:rounded-2xl p-4 shadow-sm mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" value={searchState[0]} onChange={function(e) { searchState[1](e.target.value); pageState[1](1); }}
                placeholder="Buscar por nome, email, telefone, CPF ou CNPJ..." data-testid="clients-search-input"
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6]" />
            </div>
            <button onClick={function() { sortState[1](function(s) { return s === "az" ? "za" : s === "za" ? "recent" : "az"; }); pageState[1](1); }}
              data-testid="clients-sort-button" title={sortState[0] === "az" ? "A-Z" : sortState[0] === "za" ? "Z-A" : "Recentes"}
              className="flex items-center gap-1.5 px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 whitespace-nowrap">
              {sortState[0] === "az" ? <ArrowDownAZ className="w-4 h-4" /> : sortState[0] === "za" ? <ArrowUpZA className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
              <span className="hidden sm:inline">{sortState[0] === "az" ? "A-Z" : sortState[0] === "za" ? "Z-A" : "Recentes"}</span>
            </button>
            <div className="flex gap-2 flex-wrap">
              {isSuperAdmin && <WipeClientsButton onDone={fetchClients} />}
              <button onClick={function() { showImportState[1](true); }} data-testid="clients-import-button"
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 whitespace-nowrap">
                <Upload className="w-4 h-4" /><span className="hidden sm:inline">Importar Excel</span>
              </button>
              <button onClick={function() { editingState[1](null); showFormState[1](true); }} data-testid="clients-new-button"
                className="flex items-center gap-2 px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB] whitespace-nowrap">
                <Plus className="w-4 h-4" /><span className="hidden sm:inline">Novo Cliente</span>
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-400" data-testid="clients-total-count">{totalState[0]} cliente{totalState[0] !== 1 ? "s" : ""}</p>
        </div>

        {isAdmin && <LoyaltyToggle />}

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm overflow-hidden" data-testid="clients-table-container">
          {loadingState[0] ? (
            <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
          ) : clientsState[0].length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Nenhum cliente cadastrado</p>
              <p className="text-gray-400 text-sm mt-1">Cadastre ou importe seus clientes</p>
            </div>
          ) : (
            <>
              <ClientsTable clients={clientsState[0]} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} onEdit={onEdit} onDelete={onDel} selected={selectedState[0]} onToggle={toggleSelect} onToggleAll={toggleSelectAll} />
              <ClientsMobile clients={clientsState[0]} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} onEdit={onEdit} onDelete={onDel} selected={selectedState[0]} onToggle={toggleSelect} />
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400">Pagina {pageState[0]} de {totalPages}</p>
                  <div className="flex gap-1">
                    <button disabled={pageState[0] <= 1} onClick={function() { pageState[1](pageState[0] - 1); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30">Anterior</button>
                    <button disabled={pageState[0] >= totalPages} onClick={function() { pageState[1](pageState[0] + 1); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30">Proxima</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
